/**
 * File: surveyingPage.js
 * Origin: isotect
 * Description: [TODO: Description]
 *
 * Version: v3.1.2
 * Created: 2025-12-04
 * Last Updated: 2026-02-25
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Icons
import { FaBarsProgress } from "react-icons/fa6";
import { FaClock } from "react-icons/fa6";
import { FaFileContract } from "react-icons/fa6";
import { FaMoneyBill } from "react-icons/fa6";
import { IoMdCheckmark } from "react-icons/io";

// Surveying Page
export const surveyingPage = {
    meta: {
        id: "surveying-page",
    },
    hero: {
        image: "../svgs/surveying_hero_site-isometric.svg",
        headline: "Site Survey Experts for MEP & Construction Projects",
        subheadline:
            "Accurate field data, clear documentation, and licensed professionals on every project.",
        primaryButton: {
            text: "Get Started",
            type: "primary",
        },
        secondaryButton: {
            text: "Get Estimate",
            type: "secondary",
        },
    },
    planCard: {
        title: "Schedule a Site Survey",
        cardType: "package",
        plans: [
            {
                id: "plan-sv-basic",
                header: "Basic",
                description:
                    "Great for quick site checks and essential field observations.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-sv-standard",
                header: "Standard",
                description:
                    "Perfect for detailed surveys with verification and photo documentation.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
            {
                id: "plan-sv-premium",
                header: "Premium",
                description:
                    "Designed for complex sites with full documentation and compliance review.",
                buttonText: "Compare Packages",
                buttonLink: "#comparison-table",
            },
        ],
        features: [
            {
                id: "feature-sv-1",
                icon: <FaMoneyBill />,
                text: "Flexible Pricing",
            },
            {
                id: "feature-sv-2",
                icon: <FaFileContract />,
                text: "No Long-Term Contracts",
            },
            {
                id: "feature-sv-3",
                icon: <FaBarsProgress />,
                text: "Regular Project Updates",
            },
            {
                id: "feature-sv-4",
                icon: <FaClock />,
                text: "Start Within 24-48 Hours",
            },
        ],
    },
    articles: [
        {
            id: "article-sv-1",
            reverse: true,
            image: "../svgs/surveying_reports_zoning-plan-report.svg",
            headline: "Comprehensive Field Observation Reports",
            description:
                "Receive detailed, photo-rich reports with actionable insights to support design, coordination, and construction decisions.",
        },
        {
            id: "article-sv-2",
            reverse: false,
            image: "../svgs/surveying_verification_system-phases.svg",
            headline: "Accurate Measurements & Real-World Verification",
            description:
                "Eliminate guesswork with precise field verification of existing MEP systems and site conditions.",
        },
        {
            id: "article-sv-3",
            reverse: true,
            image: "../svgs/surveying_photos_projection-views.svg",
            headline: "Complete Photo Documentation",
            description:
                "Visualize every detail with a comprehensive, organized photo gallery captured during the site survey.",
        },
        {
            id: "article-sv-4",
            reverse: false,
            image: "../svgs/surveying_regions_california-map.svg",
            headline: "Serving All Major Southern California Regions",
            description:
                "Deploy licensed site survey engineers to any California project—fast, reliable, and compliant.",
        },
    ],
    comparisonTable: {
        transpose: true,
        transposeHeader: ["Features", "Basic", "Standard", "Premium"],
        object: [
            {
                "Licensed Field Professional": <IoMdCheckmark />,
                "Dedicated Project Manager": "—",
                "On-Site Field Visit": <IoMdCheckmark />,
                "Extended or Complex Sites": "—",
                "Existing Conditions Verification": "Limited scope",
                "Field Measurements & Notes": <IoMdCheckmark />,
                "Redundant Accuracy Checks": "—",
                "MEP System Identification": "—",
                "Photo Documentation": "Key areas",
                "Marked-Up Drawings / Sketches": "—",
                "Zoning / Site Constraints Observation": "—",
                "Engineer Review": "—",
                "Senior / PE-Level Review": "—",
                "Code & Compliance Awareness": "—",
                "Coordination with Design Team": "—",
                "Construction / Pre-Design Support": "—",
                "Post-Survey Clarifications": "—",
                "Report Turnaround Time": "2–3 business days",
                "Observation Report Depth": "Summary",
            },
            {
                "Licensed Field Professional": <IoMdCheckmark />,
                "Dedicated Project Manager": "—",
                "On-Site Field Visit": <IoMdCheckmark />,
                "Extended or Complex Sites": "—",
                "Existing Conditions Verification": "Verified",
                "Field Measurements & Notes": <IoMdCheckmark />,
                "Redundant Accuracy Checks": "—",
                "MEP System Identification": <IoMdCheckmark />,
                "Photo Documentation": "Full coverage",
                "Marked-Up Drawings / Sketches": <IoMdCheckmark />,
                "Zoning / Site Constraints Observation": <IoMdCheckmark />,
                "Engineer Review": <IoMdCheckmark />,
                "Senior / PE-Level Review": "—",
                "Code & Compliance Awareness": <IoMdCheckmark />,
                "Coordination with Design Team": "—",
                "Construction / Pre-Design Support": "—",
                "Post-Survey Clarifications": "Limited",
                "Report Turnaround Time": "1–2 business days",
                "Observation Report Depth": "Detailed",
            },
            {
                "Licensed Field Professional": <IoMdCheckmark />,
                "Dedicated Project Manager": <IoMdCheckmark />,
                "On-Site Field Visit": <IoMdCheckmark />,
                "Extended or Complex Sites": <IoMdCheckmark />,
                "Existing Conditions Verification": "Comprehensive",
                "Field Measurements & Notes": <IoMdCheckmark />,
                "Redundant Accuracy Checks": <IoMdCheckmark />,
                "MEP System Identification": <IoMdCheckmark />,
                "Photo Documentation": "Full coverage",
                "Marked-Up Drawings / Sketches": <IoMdCheckmark />,
                "Zoning / Site Constraints Observation": <IoMdCheckmark />,
                "Engineer Review": <IoMdCheckmark />,
                "Senior / PE-Level Review": <IoMdCheckmark />,
                "Code & Compliance Awareness": <IoMdCheckmark />,
                "Coordination with Design Team": <IoMdCheckmark />,
                "Construction / Pre-Design Support": <IoMdCheckmark />,
                "Post-Survey Clarifications": "Included",
                "Report Turnaround Time": "Next business day",
                "Observation Report Depth": "Comprehensive",
            },
        ],
    },
    faq: [
        {
            id: "faq-sv-1",
            header: "What does the site surveying process look like?",
            content:
                "After you submit a survey request, our team reviews the project location, scope, and travel requirements. We then provide an exact quote for the service. Once approved, we schedule a licensed survey professional for your selected date and time. After the site visit is completed, your team receives the finalized report and the project is billed accordingly.",
        },
        {
            id: "faq-sv-2",
            header: "How far in advance should a survey be scheduled?",
            content:
                "Most surveys can be scheduled approximately one week from the time your work order is approved. Expedited scheduling may be available depending on location and team availability.",
        },
        {
            id: "faq-sv-3",
            header: "What deliverables will my team receive?",
            content:
                "You will receive a comprehensive field observation report prepared by a licensed professional. Reports include a summary of observations, marked-up drawings, measurement notes, system verification details, and a complete photo library documenting existing conditions.",
        },
        {
            id: "faq-sv-4",
            header: "How quickly will we receive the observation report after the site visit?",
            content:
                "Most reports are delivered within one to two business days following the survey. Larger or more complex projects may require additional processing time, which will be communicated upfront.",
        },
        {
            id: "faq-sv-5",
            header: "What areas do you service?",
            content:
                "We currently perform site surveys throughout major regions of Southern California. Travel and mileage costs are included in your upfront quote to ensure full cost transparency.",
        },
        {
            id: "faq-sv-6",
            header: "Who performs the surveys, and are they licensed?",
            content:
                "All surveys are conducted by trained and licensed MEP professionals with experience in field verification, construction coordination, and code-compliant documentation. This ensures accurate measurements, dependable insights, and consistent report quality across every project.",
        },
    ],
    cta: {
        label: "Explore Site Surveying Services",
        headline: "Upgrade Your Site Survey Workflow",
        description:
            "Reduce design errors, prevent costly rework, and keep your project moving with data you can trust.",
    },
};
